import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Crosshair, MapPinOff } from "lucide-react";
import { STATUS_KEYS, STATUS_META } from "@/components/territory/statusMeta";

const pct = (value) => `${Number(value ?? 0).toFixed(1)}%`;

/** Coverage scorecard, return per area and how tightly packed each suburb is. */
export const TerritoryScorecard = ({ data, onSelectArea }) => {
  if (!data?.coverage_scorecard) return null;
  const card = data.coverage_scorecard;
  const unplaced = card.total_doctors - (card.address_level ?? 0);

  return (
    <div className="space-y-4" data-testid="territory-scorecard">
      {/* Where the map can and cannot be trusted */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-2" data-testid="location-quality">
          <MapPinOff className="w-4 h-4 text-amber-600" />
          <p className="text-sm font-semibold">Location quality</p>
          <Badge variant="outline" className="text-[10px]">
            {card.located} of {card.total_doctors} on the map
          </Badge>
          <Badge variant="outline" className="text-[10px] text-emerald-700 border-emerald-300">
            {card.address_level} at street level
          </Badge>
          {unplaced > 0 && (
            <>
              <Badge variant="outline" className="text-[10px] text-red-600 border-red-300">
                {unplaced} pinned to a suburb or city centre
              </Badge>
              <p className="text-xs text-muted-foreground basis-full">
                Doctors without a street address share one pin, so drive times between them read as 0km. Capture an
                address on the doctor record to fix it properly.
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {/* Return per area */}
      <Card>
        <CardContent className="p-4 space-y-3" data-testid="roi-by-area">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-primary" />
            <p className="font-semibold text-sm">Return by area, last {data.window_days} days</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {data.roi_by_area.map((area) => (
              <button
                key={area.area}
                onClick={() => onSelectArea?.(area)}
                className="text-left rounded-md border p-3 hover:bg-muted/60 transition-colors"
                data-testid={`roi-area-${area.area.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium truncate">{area.area}</p>
                  <span className="text-xs font-semibold text-emerald-600">{pct(area.active_percent)} active</span>
                </div>
                <div className="h-1.5 rounded bg-muted mt-2 overflow-hidden flex">
                  {STATUS_KEYS.map((key) =>
                    area.by_status[key] ? (
                      <div
                        key={key}
                        className={STATUS_META[key].bar}
                        style={{ width: `${(area.by_status[key] / area.doctors) * 100}%` }}
                      />
                    ) : null
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1.5">
                  {area.doctors} doctors · {area.referrals_90days} referrals · {area.visits_90days} contacts
                  {area.referrals_per_visit !== null ? ` · ${area.referrals_per_visit} per contact` : ""}
                </p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Geographic efficiency */}
      {data.geographic_efficiency?.length > 0 && (
        <Card>
          <CardContent className="p-4 space-y-3" data-testid="geographic-efficiency">
            <div className="flex items-center gap-2">
              <Crosshair className="w-4 h-4 text-primary" />
              <p className="font-semibold text-sm">Tightest clusters — most doctors per kilometre driven</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {data.geographic_efficiency.map((row) => (
                <div key={row.suburb} className="rounded-md border p-3">
                  <p className="text-sm font-medium truncate">{row.suburb}</p>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {row.doctors} doctors within {row.spread_km}km · {row.actionable} need attention
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TerritoryScorecard;
